import { useEffect } from 'react';
import { DEFAULT_OG_IMAGE, DEFAULT_ROBOTS, SITE_NAME } from '~/seo/site';

export type JsonLd = Record<string, unknown> | Record<string, unknown>[];

export interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string;
  author?: string;
  robots?: string;
  ogType?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogImage?: string;
  ogUrl?: string;
  canonical?: string;
  twitterCard?: string;
  twitterTitle?: string;
  twitterDescription?: string;
  twitterImage?: string;
  /** 页面级结构化数据，挂载时写入，卸载时移除 */
  jsonLd?: JsonLd;
  /** 作为其他页面的子块嵌入时不改写文档头，避免盖掉宿主页 */
  skip?: boolean;
}

const DEFAULT_AUTHOR = '逗赛科技';
const JSON_LD_ATTR = 'data-seo-jsonld';

function upsertMeta(attr: 'name' | 'property', key: string, content: string) {
  const selector = `meta[${attr}="${key}"]`;
  let element = document.querySelector(selector) as HTMLMetaElement | null;
  if (!element) {
    element = document.createElement('meta');
    element.setAttribute(attr, key);
    document.head.appendChild(element);
  }
  element.setAttribute('content', content);
}

function upsertCanonical(href: string) {
  let link = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
  if (!link) {
    link = document.createElement('link');
    link.setAttribute('rel', 'canonical');
    document.head.appendChild(link);
  }
  link.setAttribute('href', href);
}

export const useSEO = ({
  title,
  description,
  keywords,
  author,
  robots,
  ogType,
  ogTitle,
  ogDescription,
  ogImage,
  ogUrl,
  canonical,
  twitterCard,
  twitterTitle,
  twitterDescription,
  twitterImage,
  jsonLd,
  skip,
}: SEOProps) => {
  const jsonLdText = jsonLd ? JSON.stringify(jsonLd) : '';

  useEffect(() => {
    if (skip) return;

    const resolvedTitle = ogTitle || title;
    const resolvedDescription = ogDescription || description;
    const resolvedImage = ogImage || twitterImage || DEFAULT_OG_IMAGE;
    const resolvedUrl = canonical || ogUrl;

    if (title) {
      document.title = title;
      upsertMeta('name', 'title', title);
    }

    if (description) upsertMeta('name', 'description', description);
    if (keywords) upsertMeta('name', 'keywords', keywords);
    upsertMeta('name', 'author', author || DEFAULT_AUTHOR);
    upsertMeta('name', 'robots', robots || DEFAULT_ROBOTS);

    upsertMeta('property', 'og:type', ogType || 'website');
    upsertMeta('property', 'og:site_name', SITE_NAME);
    upsertMeta('property', 'og:locale', 'zh_CN');
    if (resolvedTitle) upsertMeta('property', 'og:title', resolvedTitle);
    if (resolvedDescription) upsertMeta('property', 'og:description', resolvedDescription);
    upsertMeta('property', 'og:image', resolvedImage);
    upsertMeta('property', 'og:image:alt', resolvedTitle || SITE_NAME);
    if (ogUrl) upsertMeta('property', 'og:url', ogUrl);
    if (resolvedUrl) upsertCanonical(resolvedUrl);

    upsertMeta('name', 'twitter:card', twitterCard || 'summary');
    if (ogUrl) upsertMeta('name', 'twitter:url', ogUrl);
    if (resolvedTitle) upsertMeta('name', 'twitter:title', twitterTitle || resolvedTitle);
    if (resolvedDescription) {
      upsertMeta('name', 'twitter:description', twitterDescription || resolvedDescription);
    }
    upsertMeta('name', 'twitter:image', resolvedImage);
    upsertMeta('name', 'twitter:image:alt', resolvedTitle || SITE_NAME);
  }, [
    title,
    description,
    keywords,
    author,
    robots,
    ogType,
    ogTitle,
    ogDescription,
    ogImage,
    ogUrl,
    canonical,
    twitterCard,
    twitterTitle,
    twitterDescription,
    twitterImage,
    skip,
  ]);

  useEffect(() => {
    if (skip || !jsonLdText) return;

    // 静态路由 HTML 里可能已经注入过同一页面的结构化数据，先清掉再写入运行时的版本
    document.querySelectorAll(`script[${JSON_LD_ATTR}]`).forEach((element) => element.remove());

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.setAttribute(JSON_LD_ATTR, 'page');
    script.textContent = jsonLdText.replace(/</g, '\\u003c');
    document.head.appendChild(script);

    return () => {
      script.remove();
    };
  }, [jsonLdText, skip]);
};

export default useSEO;
